import { useState } from "react";
import { api } from "@/lib/api";
import { compressImage, humanUploadError } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package } from "lucide-react";
import { HitRing } from "@/components/loading";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";

const SEALED_TYPES_POKEMON = [
  { value: "sealed_booster", label: "Sobre (Booster Pack)" },
  { value: "sealed_booster_bundle", label: "Booster Bundle" },
  { value: "sealed_box", label: "Booster Box / Caja" },
  { value: "sealed_etb", label: "Elite Trainer Box (ETB)" },
  { value: "sealed_deck", label: "Starter Deck / Mazo pre-armado" },
  { value: "sealed_collection", label: "Colección / Caja especial / Tin" },
  { value: "sealed_blister", label: "Blíster" },
  { value: "sealed_bundle", label: "Bundle / Lote" },
  { value: "sealed_other", label: "Otro sellado (especificar en notas)" },
];

// Riftbound sella los sobres, mazos iniciales y prerelease packs. Sin ETB
// ni tin — no existe ese formato oficial en Riftbound al día de hoy.
const SEALED_TYPES_RIFTBOUND = [
  { value: "sealed_booster", label: "Sobre (Booster Pack)" },
  { value: "sealed_box", label: "Booster Box / Caja" },
  { value: "sealed_deck", label: "Starter Deck / Mazo pre-armado" },
  { value: "sealed_prerelease", label: "Prerelease Pack" },
  { value: "sealed_bundle", label: "Bundle / Lote" },
  { value: "sealed_other", label: "Otro sellado (especificar en notas)" },
];

async function uploadPublic(file) {
  const { file: toUpload } = await compressImage(file);
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("scope", "public");
  fd.append("kind", "card");
  const r = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return r.data.path;
}

/** Sealed product creation form — reuses the /custom-cards endpoint with
 * product_type != "card". Approved sealed products live in the catalog like
 * any other item; the seller then publishes them via the normal publish flow. */
const ACCESSORY_TYPES = [
  { value: "accessory_sleeves", label: "Fundas" },
  { value: "accessory_binder", label: "Carpeta" },
  { value: "accessory_deckbox", label: "Deck box" },
  { value: "accessory_playmat", label: "Playmat" },
  { value: "accessory_toploader", label: "Toploader" },
  { value: "accessory_other", label: "Otro" },
];

export function SealedProductForm({ onSubmitted, onSuccess, onCancel, game = "pokemon", accessory = false }) {
  const [form, setForm] = useState({
    product_type: accessory ? "accessory_sleeves" : "sealed_booster",
    name: "", set_name: "", serie_name: "", language: "es", notes: "",
  });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const isRiftbound = game === "riftbound";
  const SEALED_TYPES = accessory ? ACCESSORY_TYPES : isRiftbound ? SEALED_TYPES_RIFTBOUND : SEALED_TYPES_POKEMON;
  const placeholders = isRiftbound
    ? { name: "Ej: Sobre Origins", set: "Ej: Origins (OGN)", serie: "Ej: Runeterra" }
    : { name: "Ej: Sobre Escarlata y Púrpura 151", set: "Ej: 151 · Escarlata y Púrpura", serie: "Ej: Escarlata y Púrpura" };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.set_name.trim()) return toast.error("Nombre y set son obligatorios");
    if (photos.length < 2) return toast.error(accessory ? "Sube al menos 2 fotos: frente y reverso del accesorio" : "Sube al menos 2 fotos: frente y precinto de fábrica");
    setBusy(true);
    try {
      setProgress("Comprimiendo fotos…");
      const uploaded = await Promise.all(photos.map((f) => uploadPublic(f)));
      setProgress("Enviando al admin…");
      await api.post("/custom-cards", {
        name: form.name,
        set_name: form.set_name,
        serie_name: form.serie_name,
        language: form.language,
        variant: "sealed",       // ignored server-side for sealed but keeps API contract
        notes: form.notes,
        product_type: form.product_type,
        photo_paths: uploaded,
        game,
      });
      setForm({ product_type: accessory ? "accessory_sleeves" : "sealed_booster", name: "", set_name: "", serie_name: "", language: "es", notes: "" });
      setPhotos([]);
      // Legacy callback (used from the standalone Publish page).
      onSubmitted && onSubmitted();
      // New callback used from RegisterItemsPanel to refresh the list.
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || humanUploadError(err));
    } finally { setBusy(false); setProgress(""); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} data-testid="sealed-form" className="bg-[#111] border border-purple-500/40 rounded-md p-4 sm:p-6 mb-6 space-y-4">
      <div className="flex items-center gap-2 min-w-0">
        <Package size={20} className="text-purple-400 shrink-0" />
        <div className="font-display text-base sm:text-xl break-words leading-tight">Registrar producto sellado</div>
      </div>
      <div className="text-xs text-purple-200 bg-purple-500/5 border border-purple-500/20 rounded p-3">
        Los <b>productos sellados</b> (sobres, cajas, ETB, mazos…) no vienen del catálogo automático. Los cargas manualmente aquí, el admin los aprueba y luego los publicas como cualquier carta. <b>No llevan condición</b> (van sellados de fábrica).
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-neutral-400">Tipo de producto *</Label>
          <Select value={form.product_type} onValueChange={(v) => upd("product_type", v)}>
            <SelectTrigger data-testid="sealed-type" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              {SEALED_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Nombre *</Label>
          <Input data-testid="sealed-name" value={form.name} onChange={(e) => upd("name", e.target.value)} required placeholder={placeholders.name} className="bg-[#171717] border-[#262626]" />
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Set / Expansión *</Label>
          <Input data-testid="sealed-set" value={form.set_name} onChange={(e) => upd("set_name", e.target.value)} required placeholder={placeholders.set} className="bg-[#171717] border-[#262626]" />
        </div>
        <div>
          <Label className="text-xs text-neutral-400">Serie (opcional)</Label>
          <Input data-testid="sealed-serie" value={form.serie_name} onChange={(e) => upd("serie_name", e.target.value)} placeholder={placeholders.serie} className="bg-[#171717] border-[#262626]" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-neutral-400">Idioma (si aplica)</Label>
          <Select value={form.language} onValueChange={(v) => upd("language", v)}>
            <SelectTrigger data-testid="sealed-language" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">Inglés</SelectItem>
              <SelectItem value="ja">Japonés</SelectItem>
              <SelectItem value="otro">Otro</SelectItem>
              <SelectItem value="na">No aplica</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs text-neutral-400">Notas para el admin (opcional)</Label>
        <Textarea data-testid="sealed-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Detalles que ayuden al admin a verificar (ej. edición limitada, promoción, año)" className="bg-[#171717] border-[#262626]" />
      </div>

      <div>
        <Label className="text-xs text-neutral-400">Fotos reales del producto * (mínimo 2)</Label>
        <div className="text-[11px] text-neutral-500 mb-2" data-testid="sealed-photos-hint">{accessory ? "Muestra el accesorio de frente y por detrás, con su marca y medida visibles." : "Muestra el producto sellado desde varios ángulos, incluyendo el precinto/sello de fábrica."}</div>
        <MultiPhotoInput photos={photos} onChange={setPhotos} min={2} max={10} testid="sealed-photos" />
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button data-testid="sealed-submit" type="submit" disabled={busy} className="bg-purple-600 hover:bg-purple-700">
          {busy ? (
            <span className="inline-flex items-center gap-2"><HitRing tone="light" size={16} />{progress || "Enviando…"}</span>
          ) : "Enviar a revisión del admin"}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
}

export default SealedProductForm;
